import { Ref } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import { StyleObj } from '../../../../@types';
import useLocalization from '../../../../hooks/useLocalization';
import { mergeSx } from '../../../../utils/styles';

type BetslipStakeInputProps = {
  value: string;
  onToggle?: () => void;
  showKeyboard?: boolean;
  showArrow?: boolean;
  disabled?: boolean;
  inputRef?: Ref<HTMLInputElement>;
};

const sharedFontStyles = {
  fontSize: 20,
  fontWeight: 400,
  fontFamily: 'inherit',
  letterSpacing: 'normal',
  lineHeight: '1.5',
};

const styles: StyleObj = {
  inputContainer: {
    backgroundColor: 'neutral.700',
    px: 1,
    py: 0.5,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    overflow: 'visible',
  },
  placeholderCenter: {
    color: 'info.700',
    typography: 'h3',
    fontWeight: 400,
    textAlign: 'left',
  },
  labelMinimized: {
    fontSize: 11,
    color: 'neutral.300',
    textAlign: 'left',
    mb: -0.5,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'flex-start',
  },
  wrapper: {
    position: 'relative',
    display: 'inline-flex',
    minWidth: '10px',
    height: 25,
    alignItems: 'center',
  },
  ghost: {
    ...sharedFontStyles,
    visibility: 'hidden',
    whiteSpace: 'pre',
    height: 'auto',
    m: 0,
    p: 0,
  },
  input: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    '& .MuiInputBase-input': {
      ...sharedFontStyles,
      textAlign: 'left',
      color: 'info.main',
      p: 0,
      height: '100%',
    },
  },
  inputHidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
  inputRowHidden: {
    position: 'absolute',
    inset: 0,
    opacity: 0,
    pointerEvents: 'none',
    overflow: 'hidden',
  },
  currency: {
    color: 'neutral.300',
    ml: 0.5,
  },
  arrow: {
    position: 'absolute',
    bottom: '5px',
    left: '50%',
    transform: 'translateY(11px) translateX(-50%)',
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderWidth: '0 8px 6px',
    borderColor: 'transparent transparent #ddd',
    zIndex: 10,
    pointerEvents: 'none',
  },
};

const BetslipStakeInput = ({
  value,
  onToggle,
  showKeyboard,
  showArrow,
  disabled,
  inputRef,
}: BetslipStakeInputProps) => {
  const { t } = useLocalization();

  const hasValue = value && parseFloat(value) > 0;
  const showInputMode = showKeyboard || hasValue;
  const displayValue = value || '0.00';

  const handleClick = () => {
    if (!disabled) {
      onToggle?.();
    }
  };

  return (
    <Box onClick={handleClick} sx={styles.inputContainer}>
      {!showInputMode && <Typography sx={styles.placeholderCenter}>{t('stake')}</Typography>}
      {showInputMode && (
        <Typography variant='caption' sx={styles.labelMinimized}>
          {t('stake')}
        </Typography>
      )}

      <Box sx={mergeSx(!showInputMode && styles.inputRowHidden)}>
        <Box sx={styles.inputRow}>
          <Box sx={styles.wrapper}>
            <Typography component='span' sx={styles.ghost}>
              {displayValue}
            </Typography>
            {showArrow && <Box sx={styles.arrow} />}

            <InputBase
              sx={mergeSx(styles.input, !showInputMode && styles.inputHidden)}
              value={value}
              placeholder='0.00'
              disabled={disabled}
              inputRef={inputRef}
              inputProps={{ inputMode: 'none' }}
            />
          </Box>

          <Typography
            component='span'
            sx={mergeSx(
              {
                typography: 'body3',
              },
              styles.currency
            )}
          >
            EUR
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default BetslipStakeInput;
